/* eslint-disable no-empty */
import { BehaviorSubject, EMPTY } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import AddAdditionalFilesDialog from '../components/additionalFiles/AddAdditionalFilesDialog';
import ListAdditionalFilesDialog from '../components/additionalFiles/ListAdditionalFilesDialog';
import AmbraContainer from '../components/ambraContainer/AmbraContainer';
import LaunchAiRecordingDialog from '../components/caseActions/LaunchAiRecordingDialog';
import SharedImagesPopup from '../components/caseDetails/SharedImagesPopup';
import ChangeStudyTitle from '../components/changeStudyTitle/ChangeStudyTitle';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ImageWrapper from '../components/common/ImageWrapper';
import ReportEditor from '../components/common/ReportEditor';
import VideoWrapper from '../components/common/VideoWrapper';
import ConfirmSignoffPopup from '../components/confirmSignoffPopup/ConfirmSignoffPopup';
import ObjViewer from '../components/objViewer/ObjViewer';
import PdfViewer from '../components/pdfViewer/PdfViewer';
import PersonalizedVideoFilesRadiologyReport from '../components/personalizedVideoFiles/PersonalizedVideoFilesRadiologyReportDialog';
import RejectCasePopup from '../components/rejectCasePopup/RejectCasePopup';
import SubmitCaseClinicUploadPopup from '../components/submitCaseClinicUploadPopup/SubmitCaseClinicUploadPopup';
import { ACTIONS } from '../constants/logs';
import ReportViewer from '../containers/report-viewer/ReportViewer';
import Case from '../models/Case';
import { DCM, DOCS, IMAGE, PDF, THREE, VIDEO } from '../models/Resource';
import LogService from '../services/log.service';
import ReportService from '../services/report.service';
import parseFiles from '../utils/dicomUtils';

const defaultStatus = {
  isAdmin: false,
  isAssignedReviewer: false,
  isAssignedUser: false,
  reportSubmitted: false,
  status: '',
};

const defaultFileStatus = {
  additionalFiles: [],
  existingStudy: false,
  fileType: DCM,
  files: [],
  hasReport: false,
  imageCount: 0,
  reportUploaded: false,
  sharedImage: {},
  sharedImageSelected: false,
  study: null,
  uploaded: false,
  uploadedReport: [],
};

const uploadSharedInfo = {
  portalAddress: '',
  portalPassword: '',
  portalUsername: '',
  studyName: '',
};

const REPORT_FORMATS = [PDF, 'jpg', 'jpeg', 'tiff', 'png', 'txt', 'rtf', 'doc', 'docx'];

export default class CaseDetailsBloc {
  caseStatus$ = new BehaviorSubject(defaultStatus);
  loadingCase$ = new BehaviorSubject(false);
  fileStatus$ = new BehaviorSubject(defaultFileStatus);
  isSubmitting$ = new BehaviorSubject(false);
  isCaseSubmitted$ = new BehaviorSubject(false);
  isArchiving$ = new BehaviorSubject(false);
  isBypassing$ = new BehaviorSubject(false);
  shouldReturn$ = new BehaviorSubject(false);
  loadingSharedImageDetail$ = new BehaviorSubject(false);
  shouldReopenAdditionalFilesDialog$ = new BehaviorSubject(false);
  uploadSharedInfo$ = new BehaviorSubject(uploadSharedInfo);
  onlinePortalAccess$ = new BehaviorSubject(false);
  childResources$ = new BehaviorSubject([]);

  constructor({
    casesRepository,
    authRepository,
    dialogRepository,
    patientRepository,
    uploadProgressRepository,
    notificationsRepository,
    errorRepository,
    reportViewerBloc,
    userRepository,
    resourceRepository,
    workspaceRepository,
  }) {
    this.casesRepository = casesRepository;
    this.authRepository = authRepository;
    this.dialogRepository = dialogRepository;
    this.notificationsRepository = notificationsRepository;
    this.errorRepository = errorRepository;
    this.patientRepository = patientRepository;
    this.uploadProgressRepository = uploadProgressRepository;
    this.userRepository = userRepository;
    this.resourceRepository = resourceRepository;
    this.workspaceRepository = workspaceRepository;

    this.reportViewerBloc = reportViewerBloc;

    this.authRepository.getWorkspace();
    const authData = this.authRepository.auth$.getValue();

    this.caseSub = this.casesRepository.selectedCase$.pipe(filter((e) => e)).subscribe((caseData) => {
      this.caseStatus$.next({
        data: caseData,
        isAdmin: authData?.role === 'super-admin' || authData?.role === 'admin',
        isAssignedReviewer: authData?.id === caseData?.reviewer?.id,
        isAssignedUser: authData?.id === caseData?.assignee?.id,
        reportSubmitted: !!caseData.videoReport,
        status: caseData?.status?.value,
      });
    });
  }

  get auth$() {
    return this.authRepository.auth$;
  }

  get userWorkspace$() {
    return this.authRepository.userWorkspace$;
  }

  get selectedCase$() {
    return this.casesRepository.selectedCase$;
  }

  get caseToComplete$() {
    return this.casesRepository.caseToComplete$;
  }

  get userDetails$() {
    return this.userRepository.userDetails$;
  }

  openRadiologyReportDialog() {
    this.dialogRepository.loadDialog(PersonalizedVideoFilesRadiologyReport, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onReportUpload: (files) => {
        this.uploadReports(files);
        this.dialogRepository.toggleDialog(false);
      },
    });
  }

  async parseFiles(files) {
    const fileStatus = this.fileStatus$.getValue();
    try {
      const parsedFileStatus = await parseFiles(files, { ...fileStatus });
      if (parsedFileStatus) {
        this.fileStatus$.next({
          ...parsedFileStatus,
          fileType: DCM,
          hasReport: false,
        });
      }
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message,
        severity: 'error',
      });
    }
  }

  async claimCase(id) {
    const caseStudy = await this.casesRepository.claimCase(id);
    const frameUrl = await this.casesRepository.getLinkViewer(id);
    caseStudy.viewerLink = frameUrl.viewerLink;
    this.selectedCase$.next(new Case(caseStudy));
  }

  async rejectCase(id) {
    this.dialogRepository.toggleDialog(false);
    const caseStudy = await this.casesRepository.rejectCase(id);
    const frameUrl = await this.casesRepository.getLinkViewer(id);
    caseStudy.viewerLink = frameUrl.viewerLink;
    this.selectedCase$.next(new Case(caseStudy));
  }

  discardReview(id) {
    this.dialogRepository.loadDialog(ConfirmDialog, {
      cancelBtnAction: () => this.dialogRepository.toggleDialog(false),
      cancelBtnText: 'Cancel',
      message: 'Are you sure you want to discard this report?',
      okBtnAction: () => this.rejectCase(id),
      okBtnText: 'Confirm',
      title: 'Discard report',
    });
  }

  async deleteReport(reportId, caseId) {
    try {
      const reportService = new ReportService();
      await reportService.deleteCaseReport(reportId);

      this.getCaseById(caseId);
    } catch (error) {
      this.errorRepository.error$.next({
        message: 'Failed to delete report',
        severity: 'error',
      });
    }
  }

  discardReport(reportId, caseId) {
    this.dialogRepository.loadDialog(ConfirmDialog, {
      cancelBtnAction: () => this.dialogRepository.toggleDialog(false),
      cancelBtnText: 'Cancel',
      message: 'Are you sure you want to discard this report?',
      okBtnAction: async () => {
        await this.deleteReport(reportId, caseId);
        this.dialogRepository.toggleDialog(false);
      },
      okBtnText: 'Confirm',
      title: 'Discard report',
    });
  }

  async onDiscardReviewConfirm(id) {
    const caseStudy = await this.casesRepository.discardReview(id);
    const frameUrl = await this.casesRepository.getLinkViewer(id);
    caseStudy.viewerLink = frameUrl.viewerLink;
    this.selectedCase$.next(new Case(caseStudy));
    this.dialogRepository.toggleDialog(false);
  }

  async onSendForReviewConfirm(id) {
    const caseStudy = await this.casesRepository.sendForReview(id);
    const frameUrl = await this.casesRepository.getLinkViewer(id);
    caseStudy.viewerLink = frameUrl.viewerLink;
    this.selectedCase$.next(new Case(caseStudy));
    this.dialogRepository.toggleDialog(false);
  }

  async openImageSelectionDialog() {
    this.dialogRepository.loadDialog(AddAdditionalFilesDialog, {
      confirmateUpload: true,
      dialogTitle: 'Select Files',
      onCrossIconClick: () => this.closeDialog(),
      onUpload: (data) => {
        this.closeDialog();
        if (data.type === DCM) {
          this.parseFiles(data.files);
          return;
        }

        const fileType = data.type === DOCS ? PDF : data.type;

        const fileStatus = this.fileStatus$.getValue();
        this.fileStatus$.next({
          ...fileStatus,
          fileType,
          files: data.files || [],
          hasReport: false,
          imageCount: 1,
          study: data.files[0].name || '',
          uploaded: true,
        });
      },
    });
  }

  manageOnlinePortalAccess(state) {
    this.onlinePortalAccess$.next(state);
  }

  updateUploadSharedInfo(value, field, validForm = true) {
    const uploadSharedInfoData = {
      ...this.uploadSharedInfo$.getValue(),
      [field]: value,
      validForm,
    };

    this.uploadSharedInfo$.next(uploadSharedInfoData);
  }

  sendForReview(id) {
    this.dialogRepository.loadDialog(ConfirmDialog, {
      cancelBtnAction: () => this.dialogRepository.toggleDialog(false),
      cancelBtnText: 'Cancel',
      message: 'Are you sure you want to submit this report?',
      okBtnAction: () => this.onSendForReviewConfirm(id),
      okBtnText: 'Confirm',
      title: 'Submit report for review',
    });
  }

  async startReview(id) {
    const caseStudy = await this.casesRepository.startReview(id);
    const frameUrl = await this.casesRepository.getLinkViewer(id);
    caseStudy.viewerLink = frameUrl.viewerLink;
    this.selectedCase$.next(new Case(caseStudy));
  }

  cancelReview(id) {
    this.dialogRepository.loadDialog(ConfirmDialog, {
      cancelBtnAction: () => this.dialogRepository.toggleDialog(false),
      cancelBtnText: 'Cancel',
      message: 'Are you sure you want to cancel this review?',
      okBtnAction: () => this.onCancelReviewConfirm(id),
      okBtnText: 'Confirm',
      title: 'Cancel review',
    });
  }

  async onCancelReviewConfirm(id) {
    const caseStudy = await this.casesRepository.cancelReview(id);
    const frameUrl = await this.casesRepository.getLinkViewer(id);
    caseStudy.viewerLink = frameUrl.viewerLink;
    this.selectedCase$.next(new Case(caseStudy));
    this.dialogRepository.toggleDialog(false);
  }

  async signOff(id) {
    const caseDetails = this.caseStatus$.getValue().data;
    this.dialogRepository.loadDialog(ConfirmSignoffPopup, {
      materialCount: caseDetails.supplementaryMaterials.length,
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onConfirm: () => {
        this.completeSignoff(id);
      },
      patientName: caseDetails.patient.name,
    });
  }

  async completeSignoff(id) {
    const caseStudy = await this.casesRepository.signOff(id);
    const frameUrl = await this.casesRepository.getLinkViewer(id);
    caseStudy.viewerLink = frameUrl.viewerLink;
    this.selectedCase$.next(new Case(caseStudy));
    this.dialogRepository.toggleDialog(false);
  }

  dispose() {
    this.caseSub.unsubscribe();
  }

  launchRecorder(id, aiMode = false) {
    const domain = window.location.hostname;
    const token = localStorage.getItem('accessToken');
    const appParams = [`token=${token}`, `caseId=${id}`, `generateWrittenReport=${aiMode}`];

    window.location.assign(`mediphany:${domain}?${appParams.join('&')}`);
  }

  async openLaunchAiRecordingDialog(caseId) {
    this.dialogRepository.loadDialog(LaunchAiRecordingDialog, {
      onCloseIconClick: () => this.dialogRepository.toggleDialog(false),
      onConfirm: () => {
        this.launchRecorder(caseId, true);

        new LogService().create({
          action: ACTIONS.RECORDER_LAUNCHED,
          details: {
            'Ai mode': true,
          },
        });

        this.dialogRepository.toggleDialog(false);
      },
      openNormalRecording: () => {
        this.launchRecorder(caseId, false);

        new LogService().create({
          action: ACTIONS.RECORDER_LAUNCHED,
          details: {
            'Ai mode': 'false',
          },
        });

        this.dialogRepository.toggleDialog(false);
      },
    });
  }

  async rejectPendingUploadCase(id) {
    const selectedCase = this.selectedCase$.getValue();

    try {
      if (selectedCase?.status?.value !== 'pending-upload') {
        throw new Error('Not allowed to reject this case with the current status');
      }

      await this.casesRepository.rejectPendingUploadCase(id);
      this.errorRepository.error$.next({
        message: 'Case rejected',
        severity: 'info',
      });

      this.shouldReturn$.next(true);
    } catch (error) {
      this.errorRepository.error$.next({
        message: error?.message || 'Something went wrong when try to reject case',
        severity: 'error',
      });
    } finally {
      this.dialogRepository.toggleDialog(false);
    }
  }

  async submitClinicUploadFiles() {
    const selectedCase = this.selectedCase$.getValue();
    const fileStatus = this.fileStatus$.getValue();

    const payload = {
      caseId: selectedCase.id,
      files: fileStatus?.files || [],
      mainFileType: fileStatus?.fileType,
      reportFiles: fileStatus?.uploadedReport || [],
      selectedDCMId: fileStatus?.sharedImage?.resourceId,
    };

    try {
      this.isSubmitting$.next(true);
      this.uploadProgressRepository.start();
      await this.casesRepository.submitClinicUploadFiles(payload, this.uploadProgressRepository.progress$);

      this.errorRepository.error$.next({
        message: 'Case submitted',
        severity: 'info',
      });
      this.isCaseSubmitted$.next(true);
      this.uploadProgressRepository.finish();
    } catch (error) {
      this.errorRepository.error$.next({
        message: error?.message || 'Something went wrong when try to submit case',
        severity: 'error',
      });
      this.uploadProgressRepository.fail();
    } finally {
      this.isSubmitting$.next(false);
    }
  }

  openDialogRejectPendingUpload(id) {
    this.dialogRepository.loadDialog(RejectCasePopup, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onReject: () => this.rejectPendingUploadCase(id),
    });
  }

  openDialogSubmitClinicUploadFiles() {
    const { files, uploadedReport, sharedImage } = this.fileStatus$.getValue();
    const workspace = this.userWorkspace$.getValue();

    const onlinePortalAccess = this.onlinePortalAccess$.getValue();
    if (onlinePortalAccess) {
      this.validateOnlinePortalAccess();
      return;
    }

    const isSelectedImage = !!sharedImage?.resourceId || !!files?.length;
    if (!isSelectedImage) {
      this.errorRepository.error$.next({
        message: "Radiology images and report can't be blank",
        severity: 'error',
      });
      return;
    }

    if (workspace?.radiologyReportRequired) {
      if (!uploadedReport || uploadedReport.length < 1) {
        this.errorRepository.error$.next({
          message: "Radiology images and report can't be blank",
          severity: 'error',
        });
        return;
      }
    }

    this.dialogRepository.loadDialog(SubmitCaseClinicUploadPopup, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onConfirm: async () => {
        this.dialogRepository.toggleDialog(false);
        await this.submitClinicUploadFiles();
      },
    });
  }

  validateOnlinePortalAccess() {
    const { portalPassword, portalUsername, studyName, portalAddress } = this.uploadSharedInfo$.getValue();
    const onlinePortalAccess = this.onlinePortalAccess$.getValue();

    if (!onlinePortalAccess) {
      this.errorRepository.error$.next({
        message: 'Share online portal access is not allowed',
        severity: 'error',
      });
      return;
    }

    if (!portalUsername || !portalPassword || !portalAddress || !studyName) {
      this.errorRepository.error$.next({
        message: 'Please complete the online portal access fields',
        severity: 'error',
      });
      return;
    }

    this.dialogRepository.loadDialog(SubmitCaseClinicUploadPopup, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onConfirm: () => {
        this.dialogRepository.toggleDialog(false);
        this.submitOnlinePortalAccess();
      },
    });
  }

  async submitOnlinePortalAccess() {
    const selectedCase = this.selectedCase$.getValue();
    const uploadSharedInfoData = this.uploadSharedInfo$.getValue();

    delete uploadSharedInfoData.validForm;

    const payload = {
      caseId: selectedCase.id,
      uploadSharedInfo: uploadSharedInfoData,
    };

    try {
      this.isSubmitting$.next(true);
      await this.casesRepository.submitOnlinePortalAccess(payload);

      this.errorRepository.error$.next({
        message: 'Case submitted',
        severity: 'info',
      });

      this.isCaseSubmitted$.next(true);
      this.shouldReturn$.next(true);
    } catch (error) {
      this.errorRepository.error$.next({
        message: error?.message || 'Something went wrong when try to submit case',
        severity: 'error',
      });
    } finally {
      this.isSubmitting$.next(false);
    }
  }

  async archiveCase(caseId) {
    try {
      this.isArchiving$.next(true);
      await this.casesRepository.archiveCase(caseId);
      this.errorRepository.error$.next({
        message: 'Case archived!',
        severity: 'success',
      });
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'failed to archieve case',
        severity: 'error',
      });
    } finally {
      this.isArchiving$.next(false);
    }
  }

  async unarchiveCase(caseId) {
    try {
      this.isArchiving$.next(true);
      await this.casesRepository.unarchiveCase(caseId);
      this.errorRepository.error$.next({
        message: 'Case unarchived!',
        severity: 'success',
      });
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'failed to archieve case',
        severity: 'error',
      });
    } finally {
      this.isArchiving$.next(false);
    }
  }

  async bypassImagesAndReport(caseId) {
    try {
      this.isBypassing$.next(true);
      await this.casesRepository.bypassImagesAndReport(caseId);
      this.errorRepository.error$.next({
        message: 'Bypass images and report success',
        severity: 'success',
      });
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'Failed to bypass images and report',
        severity: 'error',
      });
    } finally {
      this.isBypassing$.next(false);
    }
  }

  async getCaseById(id) {
    try {
      this.loadingCase$.next(true);
      const caseStudy = await this.casesRepository.getCase(id);

      if (caseStudy.originalClinicUploadId) {
        caseStudy.originalWorkspace = await this.workspaceRepository.getWorkspaceById(caseStudy.originalClinicUploadId);
      }

      const frameUrl = await this.casesRepository.getLinkViewer(id);
      caseStudy.viewerLink = frameUrl.viewerLink;
      const currentCase = new Case(caseStudy);
      this.selectedCase$.next(currentCase);

      let childResources = [];
      if (currentCase.studyFileType === DCM && currentCase?.dicomStudy?.id) {
        childResources = (await this.patientRepository.getChildResourcesLink(currentCase.dicomStudy?.id)) || [];
      }

      this.childResources$.next(childResources);

      if (currentCase.status.value === 'uploading' && this.isCaseSubmitted$.getValue()) {
        this.shouldReturn$.next(true);
      }
    } finally {
      this.loadingCase$.next(false);
    }
  }

  openPdfReport(url) {
    this.dialogRepository.loadDialog(PdfViewer, { url });
  }

  openReportViewer(reports, dialogClasses) {
    this.dialogRepository.loadDialog(ReportViewer, {
      dialogClasses,
      reportViewerBloc: this.reportViewerBloc,
      resources: reports,
    });
  }

  selectSharedImage() {
    this.dialogRepository.loadDialog(SharedImagesPopup, {
      onCloseViewer: () => {
        this.selectSharedImage();
      },
      onSelect: (material) => {
        this.setSharedImage(material);
        this.dialogRepository.toggleDialog(false);
      },
    });
  }

  setSharedImage(sharedImage) {
    if (!sharedImage) return;
    this.fileStatus$.next({
      ...this.fileStatus$.getValue(),
      sharedImage,
      sharedImageSelected: true,
    });
  }

  removeSharedImage() {
    this.fileStatus$.next({
      ...this.fileStatus$.getValue(),
      sharedImage: {},
      sharedImageSelected: false,
    });
  }

  async openViewer(material, dialogClasses = null) {
    if (material.type === IMAGE) {
      this.dialogRepository.loadDialog(ImageWrapper, { dialogClasses, url: material.path });
    }
    if (material.type === VIDEO) {
      const videoUrl = material?.h264Path || material.path;
      this.dialogRepository.loadDialog(VideoWrapper, { isPlayOnClick: true, url: videoUrl });
    }

    if (material.type === THREE) {
      this.dialogRepository.loadDialog(ObjViewer, { url: material.path });
    }

    if (material.type === DCM) {
      try {
        // Should reload the resource info because the viewerLink may have expired
        const resourceInfo = await this.getDcmResourceByUuid(material.resource.uuid);
        this.dialogRepository.loadDialog(AmbraContainer, { onClose: () => this.dialogRepository.toggleDialog(false), url: resourceInfo?.viewerLink });
      } catch (ignore) {}
    }
  }

  async getDcmResourceByUuid(uuid) {
    try {
      this.loadingSharedImageDetail$.next(uuid);
      return await this.resourceRepository.getResourceInfo(uuid);
    } finally {
      this.loadingSharedImageDetail$.next(null);
    }
  }

  async getResourceByUuid(uuid) {
    const result = await this.resourceRepository.getResourceInfo(uuid);
    return result;
  }

  setStudy(study) {
    this.fileStatus$.next({
      ...this.fileStatus$.getValue(),
      study,
    });
  }

  openChangeTitle() {
    this.dialogRepository.loadDialog(ChangeStudyTitle, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onSave: (value) => {
        this.setStudy(value);
        this.dialogRepository.toggleDialog(false);
      },
      study: this.fileStatus$.getValue().study,
    });
  }

  uploadReports(files) {
    const fileArray = Array.from(files);
    const reports = [];

    for (let idx = 0; idx < fileArray?.length; idx++) {
      const file = fileArray[idx];

      if (file) {
        const parts = file.name.split('.');
        const extension = parts[parts.length - 1];
        if (!REPORT_FORMATS.includes(extension.toLowerCase())) {
          return this.errorRepository.error$.next({
            message: 'Uploaded report must be a pdf/doc/txt/image file',
            severity: 'error',
          });
        }
        reports.push(file);
      }
    }

    if (reports.length) {
      const fileStatus = this.fileStatus$.getValue();

      return this.fileStatus$.next({
        ...fileStatus,
        reportUploaded: true,
        uploadedReport: reports,
      });
    }

    return null;
  }

  removeReport() {
    const fileStatus = this.fileStatus$.getValue();
    this.fileStatus$.next({
      ...fileStatus,
      reportUploaded: false,
      uploadedReport: [],
    });
  }

  removeFiles() {
    const fileStatus = this.fileStatus$.getValue();
    this.fileStatus$.next({
      ...fileStatus,
      files: [],
      hasReport: false,
      imageCount: 0,
      uploaded: false,
    });
  }

  async onFilesUpload(files) {
    await this.parseFiles(files);
  }

  async populateStudy(id) {
    const fileStatus = this.fileStatus$.getValue();
    const record = await this.patientRepository.getRecord(id);
    this.status$.next({
      ...fileStatus,
      existingStudy: id,
      hasReport: record.case?.reports?.length,
      imageCount: record.imageCount,
      study: record.description,
      uploaded: true,
    });
  }

  viewMaterial(material) {
    if (material && material.resource) {
      switch (material?.resource?.type) {
        case THREE:
          this.dialogRepository.loadDialog(ObjViewer, {
            onClose: () => {
              this.checkReopenAdditionalFilesDialog();
            },
            url: material?.resource?.path,
          });
          break;
        case DCM:
          this.dialogRepository.loadDialog(AmbraContainer, {
            onClose: () => {
              this.dialogRepository.toggleDialog(false);
              this.checkReopenAdditionalFilesDialog();
            },
            url: material?.resource?.viewerLink,
          });
          break;
        case IMAGE:
          this.dialogRepository.loadDialog(ImageWrapper, {
            onClose: () => {
              this.checkReopenAdditionalFilesDialog();
            },
            url: material?.resource?.path,
          });

          break;
        case DOCS:
          this.dialogRepository.loadDialog(PdfViewer, {
            onClose: () => {
              this.checkReopenAdditionalFilesDialog();
            },
            title: material?.resource?.title || '',
            url: material?.resource?.path,
          });

          break;
        case VIDEO:
          this.dialogRepository.loadDialog(VideoWrapper, {
            onClose: () => {
              this.checkReopenAdditionalFilesDialog();
            },
            url: material?.resource?.path,
          });
          break;

        default:
          break;
      }
    }
  }

  closeDialog() {
    this.dialogRepository.toggleDialog(false);
  }

  openAdditionalFilesDialog() {
    const selectedCase = this.selectedCase$.getValue();
    const { additionalFiles = [] } = selectedCase;

    this.dialogRepository.loadDialog(ListAdditionalFilesDialog, {
      files: additionalFiles,
      onCrossIconClick: () => {
        this.shouldReopenAdditionalFilesDialog$.next(false);
        this.closeDialog();
      },
      onViewFile: (file) => {
        this.shouldReopenAdditionalFilesDialog$.next(true);
        this.closeDialog();
        this.manageAdditionalFilesViewer(file);
      },
      showDownloadButton: true,
    });
  }

  async editCaseReport(caseReport, caseId) {
    this.dialogRepository.loadDialog(
      ReportEditor,
      {
        caseId,
        caseReport,
        onClose: (updateCase = false) => {
          if (updateCase) {
            this.getCaseById(caseId);
          }

          this.dialogRepository.toggleDialog(false);
        },
        preventClose: true,
        user: this.userDetails$.getValue(),
        workspaceSignatureId: this.userWorkspace$.getValue()?.signatureId || null,
      },
      true,
      false
    );
  }

  checkReopenAdditionalFilesDialog() {
    const shouldReopen = this.shouldReopenAdditionalFilesDialog$.getValue();
    if (!shouldReopen) return;

    this.shouldReopenAdditionalFilesDialog$.next(false);
    this.openAdditionalFilesDialog();
  }

  async manageAdditionalFilesViewer(material) {
    const currentMaterial = { resource: { ...material } };

    if (material.type === DCM) {
      try {
        // Should reload the resource info because the viewerLink may have expired
        const resourceInfo = await this.getResourceByUuid(material.uuid);
        currentMaterial.resource.viewerLink = resourceInfo.viewerLink;
        this.viewMaterial(currentMaterial);
      } catch (error) {
        this.errorRepository.error$.next({
          message: error?.message || 'Failed to get resource info!',
          severity: 'error',
        });
      }
    }

    if (material.type === IMAGE) {
      currentMaterial.resource.path = material.downloadLink;
      this.viewMaterial(currentMaterial);
    }

    if (material.type === THREE) {
      currentMaterial.resource.path = material.downloadLink;
      this.viewMaterial(currentMaterial);
    }

    if (material.type === VIDEO) {
      currentMaterial.resource.path = material.downloadLink;
      this.viewMaterial(currentMaterial);
    }

    if (material.type === DOCS) {
      currentMaterial.resource.path = material.downloadLink;
      currentMaterial.resource.title = material?.title || material?.fileName || '';
      this.viewMaterial(currentMaterial);
    }
  }

  listenToCaseUpdate() {
    return this.selectedCase$.pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id),
      switchMap((selectedCase) => {
        if (selectedCase) {
          return this.notificationsRepository.getSocketConnection$().pipe(
            distinctUntilChanged((a, b) => a === b),
            switchMap((connected) => {
              if (connected) {
                this.notificationsRepository.sendMessage('join-case-details-room', { caseId: selectedCase.id });
                return this.notificationsRepository.subscribeChannel('case-updated');
              }

              return EMPTY;
            })
          );
        }

        return EMPTY;
      }),
      tap(async (event) => {
        const currentCase = this.selectedCase$.getValue();
        const currentUser = this.userRepository.userDetails$.getValue();

        if (event?.data?.clinicUploadSuccess && event?.data?.caseWorkspaceId !== currentUser.workspaceId) {
          this.errorRepository.error$.next({
            message: 'Case updated! Open the mediphany workspace to continue',
            severity: 'info',
          });
          this.shouldReturn$.next(true);
        } else if (currentCase && event.caseId === currentCase.id) {
          await this.getCaseById(currentCase.id);
          this.errorRepository.error$.next({
            message: 'Case has just been updated',
            severity: 'info',
          });
        }
      })
    );
  }

  listenToAdditionalFilesUpdate() {
    return this.selectedCase$.pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id),
      switchMap((selectedCase) => {
        if (selectedCase) {
          return this.notificationsRepository.getSocketConnection$().pipe(
            distinctUntilChanged((a, b) => a === b),
            switchMap((connected) => {
              if (connected) {
                this.notificationsRepository.sendMessage('join-case-details-room', { caseId: selectedCase.id });
                return this.notificationsRepository.subscribeChannel('additional-files-updated');
              }

              return EMPTY;
            })
          );
        }

        return EMPTY;
      }),
      tap(async (event) => {
        const currentCase = this.selectedCase$.getValue();

        if (currentCase && event.caseId === currentCase.id) {
          this.errorRepository.error$.next({
            message: 'Additional files updated!',
            severity: 'info',
          });
          await this.getCaseById(currentCase.id);
        }
      })
    );
  }
}
